import MDBox from "components/MDBox";
import { IRelPaymentsPeriod } from "interfaces/reports";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { formatDateEndOfDay } from "helpers/formatDate";
import { formatCurrency } from "helpers/utils";
import { Alert, colors, Icon, Typography } from "@mui/material";
import { TypeStatusInscricao } from "interfaces/inscricoes";
import MenuMore from "./MenuMore";
import ModalDetailsPay from "./ModalDetailsPay";
import { useState } from "react";
import { IDetailsBoleto } from "interfaces/detailsBoleto";
import { IDetailsPix } from "interfaces/detailsPix";

// imagens
import iconBoleto from "assets/images/icons/bol.png";
import iconPix from "assets/images/icons/pix.png";
import iconCartao from "assets/images/icons/car.png";
import { IDetailsCartaoEfi } from "interfaces/detailsCartao";

interface Props {
  pagos: IRelPaymentsPeriod[];
  totals: Partial<{
    [k in TypeStatusInscricao]: { qtd: number; total: number };
  }>;
}

type PaymentDetails = IDetailsBoleto | IDetailsPix | IDetailsCartaoEfi | null;

function TypePaymentoIcon({
  payment,
}: {
  payment: IRelPaymentsPeriod;
}): JSX.Element {
  if (payment.cobrancaPix) return <img src={iconPix} alt="pix" width={45} />;
  if (payment.cobrancaBoleto) {
    if (payment.cobrancaBoleto.card_mask) {
      return <img src={iconCartao} alt="cartao" width={45} />;
    } else {
      return <img src={iconBoleto} alt="boleto" width={45} />;
    }
  }
  return <Icon>payment</Icon>;
}

export default function Lancamentos({ pagos, totals }: Props) {
  const [detailsPaymentSelected, setDetailsPaymentSelected] =
    useState<PaymentDetails>(null);

  // Definicação da Tabela
  const columns: GridColDef<IRelPaymentsPeriod>[] = [
    {
      field: "dataBaixa",
      headerName: "Dt.Pagamento",
      width: 150,
      align: "center",
      valueFormatter: (params) => formatDateEndOfDay(params.value),
    },
    {
      field: "nInscrição",
      headerName: "N. Inscrição",
      width: 150,
      valueGetter: (params) => params.row.inscricaoEvento.numeroInscricao,
    },
    {
      field: "taxa",
      headerName: "Taxa",
      width: 100,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.taxa,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "totalTaxa",
      headerName: "Total Taxa",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.totalTaxa,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "desconto",
      headerName: "Desconto",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.totalDesconto,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "subTotal",
      headerName: "Sub Total",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.subTotal,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "Total",
      headerName: "Total",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.totalInscricao,
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography variant="h6">{formatCurrency(params.value)}</Typography>
      ),
    },
    {
      field: "icone",
      headerName: "-",
      width: 50,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => <TypePaymentoIcon payment={params.row} />,
    },
    {
      field: "action",
      headerName: "",
      width: 100,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: (params) => (
        <MenuMore
          payment={params.row}
          detailsPayment={setDetailsPaymentSelected}
        />
      ),
    },
  ];

  return (
    <MDBox sx={{ position: "relative", p: 2 }}>
      <ModalDetailsPay paymentDetails={detailsPaymentSelected} />
      <DataGrid
        sx={{
          flexGrow: 1,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.grey[300],
          },
        }}
        columnHeaderHeight={40}
        rows={pagos}
        columns={columns}
        autoHeight
        localeText={{
          noRowsLabel: "Nenhum registro encontrado",
        }}
        density="comfortable"
        disableRowSelectionOnClick
        pageSizeOptions={[10, 20, 50, 100]}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 10 } },
        }}
      />

      <Alert
        icon={false}
        severity="success"
        variant="standard"
        sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
      >
        <MDBox>
          <Typography variant="subtitle2" textAlign="left">
            {`${totals?.Pago?.qtd || 0} recebimentos realizados`}
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold" textAlign="left">
            Total: {formatCurrency(totals?.Pago?.total || 0)}
          </Typography>
        </MDBox>
      </Alert>
    </MDBox>
  );
}
